import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { StaleUiNotice } from '@/components/StaleUiNotice';
import { MockModeNotice } from '@/components/MockModeNotice';
import { LicenseBanner } from '@/components/License';
import { ChangePasswordModal } from '@/components/ChangePassword';
import { ErrorBoundary } from '@/components/ErrorBoundary';
import { useAuth } from '@/lib/auth';
import { Badge, Breadcrumbs, cx, type Crumb } from '@/components/ui';
import { GleeMark } from '@/components/Logo';
import {
  IconChevronDown,
  IconLogout,
  IconMenu,
  IconMonitor,
  IconMoon,
  IconShield,
  IconSidebar,
  IconSun,
  IconX,
  NAV_ICONS,
  type NavIconName,
} from '@/components/icons';
import { AppFooter, applyAccent, useBranding } from '@/lib/branding';
import { useTheme, type ThemePreference } from '@/lib/theme';

export interface NavItem {
  to: string;
  label: string;
  /** Permission required to see this item. */
  permission?: string;
  end?: boolean;
  /**
   * The icon beside the label.
   *
   * Optional, and the row still works without one -- an item added without an
   * icon lines up with the rest rather than jumping left, because the slot is
   * reserved either way. A sidebar where one item is half a centimetre out is
   * worse than one with a gap in it.
   */
  icon?: NavIconName;
}

export interface NavSection {
  title?: string;
  items: NavItem[];
}

interface AppLayoutProps {
  sections: NavSection[];
  productLabel: string;
  /** Distinguishes the two portals at a glance. */
  accent: 'client' | 'platform';
}

const SIDEBAR_KEY = 'glee.sidebar';

function NavIcon({ name }: { name?: NavIconName }) {
  const Glyph = name ? NAV_ICONS[name] : null;
  // The slot is rendered whether or not there is a glyph in it, so an item
  // added without an icon keeps its label in the same column as every other.
  return (
    <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center text-[17px]">
      {Glyph && <Glyph />}
    </span>
  );
}

/**
 * Which nav item the current path belongs to, and the trail to it.
 *
 * Derived from the same nav config the sidebar renders, so a page added to the
 * nav gets a correct title and breadcrumb without anybody remembering to
 * register it twice. Longest match wins: `/calls/live` is "Live calls", not
 * "Calls"; `/calls/123` is a detail page under "Calls".
 */
export function matchNav(
  sections: NavSection[],
  pathname: string,
): { section?: NavSection; item?: NavItem; detail: boolean } {
  let best: { section: NavSection; item: NavItem } | undefined;
  for (const section of sections) {
    for (const item of section.items) {
      const exact = pathname === item.to;
      const nested = !item.end && item.to !== '/' && pathname.startsWith(`${item.to}/`);
      if ((exact || nested) && (!best || item.to.length > best.item.to.length)) {
        best = { section, item };
      }
    }
  }
  return { ...best, detail: !!best && pathname !== best.item.to };
}

function readCollapsed(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_KEY) === 'collapsed';
  } catch {
    return false;
  }
}

const THEME_OPTIONS: Array<{ value: ThemePreference; label: string; Icon: typeof IconSun }> = [
  { value: 'system', label: 'System', Icon: IconMonitor },
  { value: 'light', label: 'Light', Icon: IconSun },
  { value: 'dark', label: 'Dark', Icon: IconMoon },
];

/** Three-way System / Light / Dark, as a segmented control. */
function ThemeSwitcher({
  value,
  onChange,
  className,
}: {
  value: ThemePreference;
  onChange: (next: ThemePreference) => void;
  className?: string;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Theme"
      className={cx(
        'inline-flex items-center gap-0.5 rounded-md border border-line bg-sunken p-0.5',
        className,
      )}
    >
      {THEME_OPTIONS.map(({ value: option, label, Icon }) => {
        const active = option === value;
        return (
          <button
            key={option}
            type="button"
            role="radio"
            aria-checked={active}
            title={`${label} theme`}
            onClick={() => onChange(option)}
            className={cx(
              'flex h-6 w-6 items-center justify-center rounded text-[13px] transition-colors',
              active
                ? 'bg-surface text-ink-900 shadow-sm ring-1 ring-line dark:bg-raised dark:text-ink-50'
                : 'text-ink-500 hover:text-ink-800 dark:text-ink-400 dark:hover:text-ink-100',
            )}
          >
            <Icon />
            <span className="sr-only">{label}</span>
          </button>
        );
      })}
    </div>
  );
}

function initialOf(name?: string | null, email?: string | null): string {
  return (name || email || '?').trim().charAt(0).toUpperCase();
}

function UserMenu({
  theme,
  onTheme,
  onSignOut,
  portalLabel,
}: {
  theme: ThemePreference;
  onTheme: (next: ThemePreference) => void;
  onSignOut: () => void;
  portalLabel: string;
}) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [changing, setChanging] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (event: MouseEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const role = user?.role?.replace(/_/g, ' ');

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-8 items-center gap-2 rounded-md pl-1 pr-1.5 text-sm text-ink-700 transition-colors hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-white/[0.06]"
      >
        <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand-600/10 text-2xs font-semibold text-brand-700 ring-1 ring-inset ring-brand-600/20 dark:bg-brand-400/15 dark:text-brand-300 dark:ring-brand-400/25">
          {initialOf(user?.name, user?.email)}
        </span>
        <span className="hidden max-w-[10rem] truncate font-medium md:inline">{user?.name}</span>
        <IconChevronDown className="hidden text-xs text-ink-400 md:inline" />
      </button>

      {open && (
        <div
          role="menu"
          className="animate-fade-in absolute right-0 top-full z-50 mt-1.5 w-64 overflow-hidden rounded-lg border border-line bg-surface shadow-pop dark:bg-raised"
        >
          <div className="border-b border-line px-3.5 py-3">
            <p className="truncate text-sm font-medium text-ink-900 dark:text-ink-50">{user?.name}</p>
            <p className="truncate text-xs text-ink-500 dark:text-ink-400">{user?.email}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              {role && <Badge className="capitalize">{role.toLowerCase()}</Badge>}
              <Badge>{portalLabel}</Badge>
            </div>
          </div>
          <div className="flex items-center justify-between gap-3 border-b border-line px-3.5 py-2.5">
            <span className="text-xs font-medium text-ink-600 dark:text-ink-300">Theme</span>
            <ThemeSwitcher value={theme} onChange={onTheme} />
          </div>
          <div className="p-1">
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                setChanging(true);
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-ink-700 transition-colors hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-white/[0.06]"
            >
              <IconShield className="text-ink-400" />
              Change password
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => {
                setOpen(false);
                onSignOut();
              }}
              className="flex w-full items-center gap-2.5 rounded-md px-2.5 py-1.5 text-left text-sm text-ink-700 transition-colors hover:bg-ink-100 dark:text-ink-200 dark:hover:bg-white/[0.06]"
            >
              <IconLogout className="text-ink-400" />
              Sign out
            </button>
          </div>
        </div>
      )}
      <ChangePasswordModal open={changing} onClose={() => setChanging(false)} />
    </div>
  );
}

export function AppLayout({ sections, productLabel, accent }: AppLayoutProps) {
  const { signOut, can } = useAuth();
  const branding = useBranding();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(readCollapsed);
  const { preference, setPreference } = useTheme();

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_KEY, collapsed ? 'collapsed' : 'expanded');
    } catch {
      // Not persisted; still collapses for this session.
    }
  }, [collapsed]);

  // A route change closes the mobile drawer, whatever caused it.
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  // The operator's accent, into the stylesheet. Here rather than in a
  // provider because the shell is the only thing that renders on every
  // authenticated page, and the login screen sets it for itself.
  useEffect(() => {
    applyAccent(branding.accent, branding.accent_to);
  }, [branding.accent, branding.accent_to]);

  const visible = sections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.permission || can(item.permission)),
    }))
    .filter((section) => section.items.length > 0);

  const match = useMemo(() => matchNav(sections, location.pathname), [sections, location.pathname]);

  const crumbs: Crumb[] = [];
  if (match.section?.title) crumbs.push({ label: match.section.title });
  if (match.item) {
    crumbs.push({ label: match.item.label, to: match.detail ? match.item.to : undefined });
    if (match.detail) crumbs.push({ label: 'Details' });
  }

  // The tab title names the page. Twelve tabs all called "Glee AI" is how
  // somebody ends up acting on the wrong client.
  const pageTitle = match.item?.label;
  useEffect(() => {
    document.title = pageTitle
      ? `${pageTitle} · ${branding.product_name}`
      : branding.product_name;
  }, [pageTitle, branding.product_name]);

  const handleSignOut = async () => {
    await signOut();
    navigate('/login', { replace: true });
  };

  return (
    <div className="flex h-full bg-sunken">
      {/* Backdrop for the mobile drawer */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-ink-950/40 backdrop-blur-[1px] lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/*
        A dark navigation panel, in both themes. The first version of the
        redesign used a white rail with grey regular-weight labels, and the
        operator's verdict was that the menu "shows white" and its text is not
        bold enough to read at a glance. A dark rail separates navigation from
        content without a border doing all the work, and lets the labels be
        white and semibold. Colour stays restrained: the brand accent appears
        only on the page you are on. On desktop it collapses to an icon rail
        (remembered per browser); the mobile drawer is always full width.
      */}
      <aside
        className={cx(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r border-white/[0.06] bg-[#0b1220] text-slate-300',
          'transition-[transform,width] duration-200 ease-out lg:static lg:translate-x-0',
          collapsed ? 'lg:w-[3.75rem]' : 'lg:w-60',
          mobileOpen ? 'translate-x-0 shadow-pop' : '-translate-x-full',
        )}
      >
        <div
          className={cx(
            'flex h-14 shrink-0 items-center gap-2.5 border-b border-white/[0.07] px-4',
            collapsed && 'lg:justify-center lg:px-0',
          )}
        >
          {branding.logo_url ? (
            <img
              src={branding.logo_url}
              alt={branding.product_name}
              className="h-7 w-7 shrink-0 rounded-md object-contain"
            />
          ) : (
            <GleeMark size={26} className="shrink-0" />
          )}
          <div className={cx('min-w-0 flex-1', collapsed && 'lg:hidden')}>
            <p className="truncate text-[15px] font-bold leading-tight tracking-tight text-white">
              {branding.product_name}
            </p>
            <p className="truncate text-2xs font-semibold uppercase leading-tight tracking-[0.1em] text-brand-300">
              {productLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setMobileOpen(false)}
            aria-label="Close navigation"
            className="rounded-md p-1.5 text-slate-400 hover:bg-white/10 hover:text-white lg:hidden"
          >
            <IconX />
          </button>
        </div>

        <nav
          aria-label="Main"
          className={cx('flex-1 overflow-y-auto overflow-x-hidden py-3 [scrollbar-color:rgba(255,255,255,0.14)_transparent] [scrollbar-width:thin]', collapsed ? 'px-3 lg:px-2' : 'px-3')}
        >
          {visible.map((section, index) => (
            <div key={section.title ?? index} className={index > 0 ? 'mt-5' : undefined}>
              {section.title && (
                <p
                  className={cx(
                    'mb-1.5 px-2.5 text-[11px] font-bold uppercase tracking-[0.1em] text-slate-500',
                    collapsed && 'lg:hidden',
                  )}
                >
                  {section.title}
                </p>
              )}
              {/* In the rail there is no room for a heading, so a hairline
                  marks where one section ends and the next begins. */}
              {index > 0 && collapsed && (
                <div className="mx-2 mb-2 hidden border-t border-white/[0.08] lg:block" />
              )}
              <ul className="space-y-0.5">
                {section.items.map((item) => (
                  <li key={item.to}>
                    {/*
                      The active row is marked by a bar at its edge as well as
                      by its fill. Fill alone is one step of contrast, which is
                      the first thing to disappear on a dim laptop screen in a
                      bright room -- and "which page am I on" is the one
                      question a sidebar exists to answer.
                    */}
                    <NavLink
                      to={item.to}
                      end={item.end}
                      title={collapsed ? item.label : undefined}
                      onClick={() => setMobileOpen(false)}
                      className={({ isActive }) =>
                        cx(
                          'group relative flex h-9 items-center gap-3 rounded-lg px-3 text-[13.5px] font-semibold transition-colors duration-100',
                          collapsed && 'lg:justify-center lg:px-0',
                          isActive
                            ? 'bg-brand-600 text-white shadow-sm shadow-black/20'
                            : 'text-slate-300 hover:bg-white/[0.07] hover:text-white',
                        )
                      }
                    >
                      {({ isActive }) => (
                        <>
                          <span
                            className={cx(
                              isActive
                                ? 'text-white'
                                : 'text-slate-400 group-hover:text-white',
                            )}
                          >
                            {/* The slot is always there, icon or not, so a new
                                item without one does not shunt its label out of
                                line with everything above it. */}
                            <NavIcon name={item.icon} />
                          </span>
                          <span className={cx('truncate', collapsed && 'lg:sr-only')}>{item.label}</span>
                        </>
                      )}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </nav>

        <div className="hidden shrink-0 border-t border-white/[0.07] p-2 lg:block">
          <button
            type="button"
            onClick={() => setCollapsed((c) => !c)}
            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            className={cx(
              'flex h-9 w-full items-center gap-3 rounded-lg px-3 text-xs font-semibold text-slate-400 transition-colors hover:bg-white/[0.07] hover:text-white',
              collapsed && 'justify-center px-0',
            )}
          >
            <IconSidebar className="shrink-0 text-[15px]" />
            {!collapsed && 'Collapse'}
          </button>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-14 shrink-0 items-center gap-3 border-b border-line bg-surface px-3 sm:px-4 lg:px-6">
          <button
            type="button"
            onClick={() => setMobileOpen(true)}
            aria-label="Open navigation"
            className="-ml-1 rounded-md p-1.5 text-lg text-ink-600 hover:bg-ink-100 lg:hidden dark:text-ink-300 dark:hover:bg-white/[0.06]"
          >
            <IconMenu />
          </button>

          {/* On a phone there is no room for both, and knowing which product
              you are in matters less than which page. */}
          <div className="min-w-0 flex-1">
            {crumbs.length > 0 ? (
              <Breadcrumbs items={crumbs} className="text-sm" />
            ) : (
              <span className="truncate text-sm font-semibold text-ink-900 dark:text-ink-50">
                {branding.product_name}
              </span>
            )}
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <Badge
              tone={accent === 'platform' ? 'purple' : 'blue'}
              dot
              className="hidden sm:inline-flex"
              title={accent === 'platform' ? 'Platform administration' : 'Client portal'}
            >
              {productLabel}
            </Badge>
            <ThemeSwitcher
              value={preference}
              onChange={setPreference}
              className="hidden md:inline-flex"
            />
            <div className="hidden h-5 w-px bg-line sm:block" />
            <UserMenu
              theme={preference}
              onTheme={setPreference}
              onSignOut={handleSignOut}
              portalLabel={productLabel}
            />
          </div>
        </header>

        <main className="min-w-0 flex-1 overflow-y-auto">
          <div className="mx-auto w-full max-w-[110rem] px-3 py-4 sm:px-5 lg:px-6 lg:py-5">
            {/* Above the page, on every page: a browser holding an old bundle
                looks exactly like a feature that was never built. */}
            <MockModeNotice />
            <StaleUiNotice />
            <LicenseBanner />
            {/* Keyed on the path so navigating away clears a previous
                page's error rather than showing it on the new one. */}
            <ErrorBoundary key={location.pathname} scope="this page">
              <Outlet />
            </ErrorBoundary>
          </div>
        </main>

        {/* Inside the scroll container's sibling, not the container: a footer
            that scrolls with a long table is one nobody ever sees, and the
            version in it is the first question of every bug report. */}
        <AppFooter className="shrink-0 bg-surface" />
      </div>
    </div>
  );
}

export function PageContainer({ children }: { children: ReactNode }) {
  return <div className="mx-auto max-w-7xl">{children}</div>;
}
